import React, { useState, useRef, useCallback } from 'react';

interface AskNeuTextButtonProps {
  onNeuResponse: (question: string, response: string) => void;
  disabled?: boolean;
}

const AskNeuTextButton: React.FC<AskNeuTextButtonProps> = ({ onNeuResponse, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      // Close and reset
      setIsOpen(false);
      setQuestion('');
      setError(null);
    } else {
      // Open and focus
      setIsOpen(true);
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!question.trim() || isSearching) return;

    try {
      setIsSearching(true);
      setError(null);

      // Send the question directly to Neural Search (same as voice version)
      const searchResponse = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: question.trim(),
          conversationHistory: [], // Fresh conversation for each Ask Neu query
          inputMethod: 'text',
        }),
      });

      if (!searchResponse.ok) {
        throw new Error('Failed to get response from Neu');
      }

      const searchResult = await searchResponse.json() as { content: string };
      const response = searchResult.content || 'Sorry, I could not find relevant information.';

      // Pass both question and response to parent
      onNeuResponse(question.trim(), response);

      // Reset and close
      setQuestion('');
      setIsOpen(false);

    } catch (err) {
      console.error('Ask Neu Text error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process question');
    } finally {
      setIsSearching(false);
    }
  }, [question, isSearching, onNeuResponse]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleToggle();
    }
  }, [handleSubmit, handleToggle]);

  // Auto-clear error after 3 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="relative">
      {/* Main Button */}
      <button
        onClick={handleToggle}
        disabled={disabled}
        className={`p-2 rounded hover:bg-gray-100 transition-all duration-200 ${
          isOpen ? 'bg-purple-100 text-purple-600' : 'text-gray-600'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        title="Ask Neu (Text)"
      >
        <div className="flex items-center space-x-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
          </svg>
          <span className="text-xs font-medium">Neu</span>
        </div>
      </button>

      {/* Dropdown Text Input */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-20 min-w-80">
          <div className="flex items-start space-x-2">
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Neu about your transcripts..."
                className="w-full resize-none border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={2}
                disabled={isSearching}
              />
              <div className="flex justify-between items-center mt-2">
                <div className="text-xs text-gray-500">
                  Press Enter to send, Shift+Enter for new line, Esc to close
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={handleToggle}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!question.trim() || isSearching}
                    className={`px-3 py-1 rounded text-xs font-medium transition-all duration-200 ${
                      isSearching
                        ? 'bg-purple-100 text-purple-600'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSearching ? (
                      <div className="flex items-center space-x-1">
                        <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-purple-600"></div>
                        <span>Thinking...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                        </svg>
                        <span>Send</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Backdrop to close dropdown when clicking outside */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={handleToggle}
        />
      )}
    </div>
  );
};

export default AskNeuTextButton;
